// Collapse a label that repeats itself into a single segment.
//
// ── MEASURED, NOT INVENTED ──────────────────────────────────────────────────
//
// A real Wix contact form ships aria-label="Phone. Phone" on its phone input,
// so the captured lead carried the field key "Phone. Phone" into the customer's
// inbox (bettergardens.ae, 2026-09-21). The same doubling appears on the
// country-code button ("Phone. Phone. Select a country code"), so it is a
// builder idiom — a widget composing <field label> + <sub label> and emitting
// both — not a one-off typo in one customer's form.
//
// The rule is therefore GENERIC: split on sentence/segment separators, drop a
// segment equal to one already seen, rejoin. It says nothing about "Phone"; a
// special case for that word would fix one field on one builder and leave
// "Email. Email" and "Name. Name" to be discovered later by a customer.
//
// Deliberately conservative in three ways:
//   - only ADJACENT-or-earlier EXACT repeats (case-insensitive) are dropped, so
//     "Address line 1. Address line 2" keeps both segments;
//   - a label with no separator is returned untouched — "Phone Phone" as a bare
//     string is left alone, because splitting on whitespace would wreck
//     legitimate labels like "First Name";
//   - if every segment is a repeat, the first is kept rather than returning ''.
var LABEL_SEGMENT_SPLIT = /\s*[.;|/–—]\s*|\s+-\s+/;
export function normaliseLabel(raw) {
  var s = String(raw == null ? '' : raw).trim();
  if (!s) return '';
  var parts = s.split(LABEL_SEGMENT_SPLIT);
  if (parts.length < 2) return s;
  var out = [];
  var seen = {};
  for (var i = 0; i < parts.length; i++) {
    var p = parts[i].trim();
    if (!p) continue;
    var k = p.toLowerCase();
    if (seen[k]) continue;
    seen[k] = 1;
    out.push(p);
  }
  if (out.length === 0) return s;
  return out.join(' ');
}

// Resolve a human-readable label for a form field. Order matches the M2 spec.
export function labelFor(input) {
  if (!input) return 'field';
  try {
    var aria = input.getAttribute && input.getAttribute('aria-label');
    if (aria) return normaliseLabel(aria);
    var id = input.id;
    if (id && typeof document !== 'undefined') {
      var sel = 'label[for="' + (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id) + '"]';
      var lbl = document.querySelector(sel);
      if (lbl && lbl.textContent) return lbl.textContent.trim();
    }
    if (input.closest) {
      var parent = input.closest('label');
      if (parent && parent.textContent) return parent.textContent.trim();
    }
    if (input.placeholder) return String(input.placeholder).trim();
    if (input.name) return String(input.name).trim();
    var da = input.getAttribute && (input.getAttribute('data-ft-label') || input.getAttribute('data-label'));
    if (da) return String(da).trim();
    // `id` before the bare type fallback. A builder that ships neither a name
    // nor any label-ish attribute still gives us SOMETHING specific, where
    // `type` collapses every text input on the form to the single key "text"
    // and they overwrite each other in the {label: value} dict.
    //
    // Deliberately LAST-but-one, not first: on Wix the id is
    // `form-field-input-<uuid>`, which is worse than useless as a lead field
    // label and would defeat findEmail()/hasRealContactField(), both of which
    // match on the KEY. aria-label ("Email", "Name") is already checked first
    // and is what actually carries meaning there.
    if (id) return String(id).trim();
    return input.type || 'field';
  } catch (e) {
    return 'field';
  }
}

// True for inputs whose value is a secret we must NEVER read or transmit:
// a DOM type=password, OR a field name/label that looks password-like
// (custom widgets that don't render a real type=password). Centralised here
// so the SAME rule guards every value-reading path — extractFields (native
// submit), the change buffer, and the fetch/XHR body intercept in ft.js.
// Deliberately broad (matches "passport"/"passenger" too): over-stripping a
// field is harmless; leaking a password is not.
var PASSWORD_FIELD_RE = /pass(word)?|pwd/i;
export function isPasswordField(name, type) {
  if ((type || '').toLowerCase() === 'password') return true;
  return PASSWORD_FIELD_RE.test(String(name == null ? '' : name));
}

// Pull a {label: value} dict from a form element. Skips secret/non-data
// inputs; checkboxes and radios are only included when checked.
export function extractFields(form) {
  var out = {};
  if (!form || !form.querySelectorAll) return out;
  try {
    var inputs = form.querySelectorAll('input, textarea, select');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      var t = (el.type || '').toLowerCase();
      if (t === 'submit' || t === 'button' || t === 'file' || t === 'reset' || t === 'image' || t === 'hidden') continue;
      if (t === 'checkbox' || t === 'radio') { if (!el.checked) continue; }
      var key = labelFor(el);
      // Never read a secret field (type=password or a password-like name/label).
      if (isPasswordField(el.name || el.id || key, t)) continue;
      if (key && el.value != null && el.value !== '') {
        out[key] = String(el.value);
      }
    }
  } catch (e) { /* ignore */ }
  return out;
}
